import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  BarChart2, TrendingUp, Users, AlertCircle, ArrowRight,
} from '../../../components/icons';
import { useAuth } from '../../../contexts/AuthContext';
import { getCoachIdForUser, loadCoachTeams } from '../../../lib/coach-teams';
import { loadTeamResults, computeTeamAverages } from '../../../lib/teams';
import {
  computeFullTeamMetrics,
  loadInsightSnapshots,
  metricDefinitions,
  getMetricLevel,
} from '../../../lib/team-insights';
import { LoadingSpinner } from '../../../components/LoadingSpinner';
import type { Team } from '../../../types/team';
import type { TeamMetrics, InsightSnapshot } from '../../../lib/team-insights';

type ReportMode = 'time' | 'cross-team';

export function CoachTeamReportsTR() {
  const navigate = useNavigate();
  const { user, isLoading: authLoading } = useAuth();
  const [isLoading, setIsLoading] = useState(true);
  const [teams, setTeams] = useState<Team[]>([]);
  const [coachId, setCoachId] = useState<string | null>(null);
  const [mode, setMode] = useState<ReportMode>('time');
  const [error, setError] = useState<string | null>(null);

  // Time comparison state
  const [selectedTeamId, setSelectedTeamId] = useState<string>('');
  const [snapshots, setSnapshots] = useState<InsightSnapshot[]>([]);
  const [snapshotA, setSnapshotA] = useState<string>('');
  const [snapshotB, setSnapshotB] = useState<string>('');

  // Cross-team state
  const [teamAId, setTeamAId] = useState<string>('');
  const [teamBId, setTeamBId] = useState<string>('');
  const [teamAMetrics, setTeamAMetrics] = useState<TeamMetrics | null>(null);
  const [teamBMetrics, setTeamBMetrics] = useState<TeamMetrics | null>(null);
  const [teamAName, setTeamAName] = useState('');
  const [teamBName, setTeamBName] = useState('');
  const [comparing, setComparing] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/tr/login'); return; }

    const init = async () => {
      try {
        const cId = await getCoachIdForUser(user.id);
        if (!cId) { navigate('/tr/coach'); return; }
        setCoachId(cId);

        const loaded = await loadCoachTeams(cId);
        setTeams(loaded.filter(t => t.type === 'team'));
      } catch (err) {
        setError('Takimlar yuklenemedi');
      } finally {
        setIsLoading(false);
      }
    };

    init();
  }, [user, authLoading, navigate]);

  const handleTeamSelect = async (teamId: string) => {
    setSelectedTeamId(teamId);
    setSnapshotA('');
    setSnapshotB('');
    if (!teamId) { setSnapshots([]); return; }

    try {
      const loaded = await loadInsightSnapshots(teamId);
      setSnapshots(loaded);
    } catch (err) {
      setError('Anlık görüntüler yüklenemedi');
    }
  };

  const handleCrossTeamCompare = async () => {
    if (!teamAId || !teamBId) return;
    setComparing(true);
    setError(null);

    try {
      const [resultsA, resultsB] = await Promise.all([
        loadTeamResults(teamAId),
        loadTeamResults(teamBId),
      ]);

      const teamA = teams.find(t => t.id === teamAId);
      const teamB = teams.find(t => t.id === teamBId);

      setTeamAName(teamA?.name || 'Takim A');
      setTeamBName(teamB?.name || 'Takim B');

      const avgA = computeTeamAverages(teamAId, teamA?.name || '', resultsA);
      const avgB = computeTeamAverages(teamBId, teamB?.name || '', resultsB);

      setTeamAMetrics(computeFullTeamMetrics(avgA, resultsA));
      setTeamBMetrics(computeFullTeamMetrics(avgB, resultsB));
    } catch (err: any) {
      setError(err.message || 'Takimlar karsilastirilamadi');
    } finally {
      setComparing(false);
    }
  };

  if (authLoading || isLoading) {
    return <LoadingSpinner message="Yukleniyor..." />;
  }

  const snapshotAData = snapshots.find(s => s.snapshotDate === snapshotA);
  const snapshotBData = snapshots.find(s => s.snapshotDate === snapshotB);

  return (
    <div className="min-h-screen freud-bg">
      <header className="bg-white/80 backdrop-blur-sm border-b border-cream-300">
        <div className="max-w-7xl mx-auto px-4 py-4">
          <div className="flex items-center gap-3">
            <div className="p-2.5 bg-warm-500 rounded-xl shadow-soft">
              <BarChart2 className="w-6 h-6 text-white" />
            </div>
            <div>
              <h1 className="text-2xl font-bold text-chocolate-900">Karşılaştırma Raporları</h1>
              <p className="text-sm text-chocolate-500">Zaman içinde veya takımlar arası performansı karşılaştırın</p>
            </div>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 py-8">
        {error && (
          <div className="bg-coral-100 border border-coral-200 text-coral-700 px-4 py-3 rounded-xl mb-6 flex items-center gap-2">
            <AlertCircle className="w-5 h-5" />
            {error}
          </div>
        )}

        {teams.length === 0 ? (
          <div className="freud-card-elevated p-12 text-center">
            <BarChart2 className="w-16 h-16 text-cream-400 mx-auto mb-4" />
            <h3 className="text-lg font-semibold text-chocolate-700 mb-2">Kullanılabilir Takım Yok</h3>
            <p className="text-chocolate-500">Rapor oluşturmaya başlamak için takımlar oluşturun ve üyeler ekleyin.</p>
          </div>
        ) : (
          <>
            <div className="flex gap-2 mb-8">
              <button
                onClick={() => setMode('time')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                  mode === 'time' ? 'bg-chocolate-800 text-white' : 'bg-cream-200 text-chocolate-700 hover:bg-cream-300'
                }`}
              >
                <TrendingUp className="w-4 h-4" />
                Zaman Karşılaştırması
              </button>
              <button
                onClick={() => setMode('cross-team')}
                className={`flex items-center gap-2 px-4 py-2.5 rounded-xl font-medium transition-all ${
                  mode === 'cross-team' ? 'bg-chocolate-800 text-white' : 'bg-cream-200 text-chocolate-700 hover:bg-cream-300'
                }`}
              >
                <Users className="w-4 h-4" />
                Takımlar Arası Karşılaştırma
              </button>
            </div>

            {mode === 'time' && (
              <TimeComparison
                teams={teams}
                selectedTeamId={selectedTeamId}
                onTeamSelect={handleTeamSelect}
                snapshots={snapshots}
                snapshotA={snapshotA}
                snapshotB={snapshotB}
                onSnapshotAChange={setSnapshotA}
                onSnapshotBChange={setSnapshotB}
                snapshotAData={snapshotAData}
                snapshotBData={snapshotBData}
              />
            )}

            {mode === 'cross-team' && (
              <CrossTeamComparison
                teams={teams}
                teamAId={teamAId}
                teamBId={teamBId}
                onTeamAChange={setTeamAId}
                onTeamBChange={setTeamBId}
                onCompare={handleCrossTeamCompare}
                comparing={comparing}
                teamAMetrics={teamAMetrics}
                teamBMetrics={teamBMetrics}
                teamAName={teamAName}
                teamBName={teamBName}
              />
            )}
          </>
        )}
      </main>
    </div>
  );
}

function TimeComparison({
  teams,
  selectedTeamId,
  onTeamSelect,
  snapshots,
  snapshotA,
  snapshotB,
  onSnapshotAChange,
  onSnapshotBChange,
  snapshotAData,
  snapshotBData,
}: {
  teams: Team[];
  selectedTeamId: string;
  onTeamSelect: (id: string) => void;
  snapshots: InsightSnapshot[];
  snapshotA: string;
  snapshotB: string;
  onSnapshotAChange: (v: string) => void;
  onSnapshotBChange: (v: string) => void;
  snapshotAData?: InsightSnapshot;
  snapshotBData?: InsightSnapshot;
}) {
  return (
    <div className="space-y-6">
      <div className="freud-card-elevated p-6">
        <h3 className="font-semibold text-chocolate-800 mb-4">Takım ve Tarihleri Seçin</h3>
        <div className="grid md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-chocolate-700 mb-1">Takim</label>
            <select
              value={selectedTeamId}
              onChange={(e) => onTeamSelect(e.target.value)}
              className="freud-input"
            >
              <option value="">Takim sec...</option>
              {teams.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-chocolate-700 mb-1">Başlangıç (Önceki)</label>
            <select
              value={snapshotA}
              onChange={(e) => onSnapshotAChange(e.target.value)}
              disabled={snapshots.length === 0}
              className="freud-input"
            >
              <option value="">Tarih sec...</option>
              {snapshots.map(s => (
                <option key={s.snapshotDate} value={s.snapshotDate}>{s.snapshotDate}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-chocolate-700 mb-1">Bitiş (Sonraki)</label>
            <select
              value={snapshotB}
              onChange={(e) => onSnapshotBChange(e.target.value)}
              disabled={snapshots.length === 0}
              className="freud-input"
            >
              <option value="">Tarih sec...</option>
              {snapshots.map(s => (
                <option key={s.snapshotDate} value={s.snapshotDate}>{s.snapshotDate}</option>
              ))}
            </select>
          </div>
        </div>

        {selectedTeamId && snapshots.length === 0 && (
          <p className="text-sm text-chocolate-400 mt-4">Bu takim icin henuz anlик goruntu bulunmuyor. Anlик goruntu olusturmak icin Takim Icgorulerini ziyaret edin.</p>
        )}
      </div>

      {snapshotAData && snapshotBData && (
        <ComparisonGrid
          labelA={snapshotAData.snapshotDate}
          labelB={snapshotBData.snapshotDate}
          metricsA={snapshotAData.metrics}
          metricsB={snapshotBData.metrics}
        />
      )}
    </div>
  );
}

function CrossTeamComparison({
  teams,
  teamAId,
  teamBId,
  onTeamAChange,
  onTeamBChange,
  onCompare,
  comparing,
  teamAMetrics,
  teamBMetrics,
  teamAName,
  teamBName,
}: {
  teams: Team[];
  teamAId: string;
  teamBId: string;
  onTeamAChange: (v: string) => void;
  onTeamBChange: (v: string) => void;
  onCompare: () => void;
  comparing: boolean;
  teamAMetrics: TeamMetrics | null;
  teamBMetrics: TeamMetrics | null;
  teamAName: string;
  teamBName: string;
}) {
  return (
    <div className="space-y-6">
      <div className="freud-card-elevated p-6">
        <h3 className="font-semibold text-chocolate-800 mb-4">Karşılaştırmak İçin İki Takım Seçin</h3>
        <div className="grid md:grid-cols-3 gap-4 items-end">
          <div>
            <label className="block text-sm font-medium text-chocolate-700 mb-1">Takim A</label>
            <select
              value={teamAId}
              onChange={(e) => onTeamAChange(e.target.value)}
              className="freud-input"
            >
              <option value="">Takim sec...</option>
              {teams.map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <div>
            <label className="block text-sm font-medium text-chocolate-700 mb-1">Takim B</label>
            <select
              value={teamBId}
              onChange={(e) => onTeamBChange(e.target.value)}
              className="freud-input"
            >
              <option value="">Takim sec...</option>
              {teams.filter(t => t.id !== teamAId).map(t => (
                <option key={t.id} value={t.id}>{t.name}</option>
              ))}
            </select>
          </div>
          <button
            onClick={onCompare}
            disabled={!teamAId || !teamBId || comparing}
            className="freud-btn-primary disabled:opacity-50"
          >
            {comparing ? 'Karşılaştırılıyor...' : 'Karşılaştır'}
          </button>
        </div>
      </div>

      {teamAMetrics && teamBMetrics && (
        <ComparisonGrid
          labelA={teamAName}
          labelB={teamBName}
          metricsA={teamAMetrics}
          metricsB={teamBMetrics}
        />
      )}
    </div>
  );
}

function ComparisonGrid({
  labelA,
  labelB,
  metricsA,
  metricsB,
}: {
  labelA: string;
  labelB: string;
  metricsA: TeamMetrics;
  metricsB: TeamMetrics;
}) {
  const categories = [
    { key: 'capability' as const, label: 'Yetenekler' },
    { key: 'harmonic' as const, label: 'Duygusal Zeka' },
    { key: 'distribution' as const, label: 'Takim Dagilimi' },
  ];

  return (
    <div className="space-y-6">
      {categories.map(cat => {
        const defs = metricDefinitions.filter(d => d.category === cat.key);
        return (
          <div key={cat.key} className="freud-card-elevated overflow-hidden">
            <div className="px-6 py-3 bg-cream-100 border-b border-cream-200">
              <h3 className="font-semibold text-chocolate-800">{cat.label}</h3>
            </div>
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-cream-200">
                    <th className="text-left px-6 py-3 text-sm font-semibold text-chocolate-700">Metrik</th>
                    <th className="text-center px-6 py-3 text-sm font-semibold text-chocolate-700">{labelA}</th>
                    <th className="text-center px-4 py-3 text-sm text-chocolate-400"><ArrowRight className="w-4 h-4 mx-auto" /></th>
                    <th className="text-center px-6 py-3 text-sm font-semibold text-chocolate-700">{labelB}</th>
                    <th className="text-center px-6 py-3 text-sm font-semibold text-chocolate-700">Fark</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-cream-100">
                  {defs.map(def => {
                    const valA = metricsA[def.key] as number;
                    const valB = metricsB[def.key] as number;
                    const delta = valB - valA;
                    const improving = def.higherIsBetter ? delta > 0 : delta < 0;
                    const declining = def.higherIsBetter ? delta < 0 : delta > 0;

                    return (
                      <tr key={def.key} className="hover:bg-cream-50">
                        <td className="px-6 py-3">
                          <span className="text-sm font-medium text-chocolate-800">{def.label}</span>
                        </td>
                        <td className="text-center px-6 py-3">
                          <span className="text-sm font-semibold text-chocolate-700">{valA}</span>
                          <span className={`ml-2 text-xs ${getMetricLevel(valA, def.higherIsBetter).color}`}>
                            {getMetricLevel(valA, def.higherIsBetter).label}
                          </span>
                        </td>
                        <td className="text-center px-4 py-3">
                          <ArrowRight className="w-3 h-3 text-chocolate-300 mx-auto" />
                        </td>
                        <td className="text-center px-6 py-3">
                          <span className="text-sm font-semibold text-chocolate-700">{valB}</span>
                          <span className={`ml-2 text-xs ${getMetricLevel(valB, def.higherIsBetter).color}`}>
                            {getMetricLevel(valB, def.higherIsBetter).label}
                          </span>
                        </td>
                        <td className="text-center px-6 py-3">
                          <span className={`text-sm font-bold px-2 py-0.5 rounded ${
                            delta === 0 ? 'text-chocolate-400' :
                            improving ? 'text-emerald-700 bg-emerald-50' :
                            declining ? 'text-red-700 bg-red-50' : 'text-chocolate-400'
                          }`}>
                            {delta > 0 ? '+' : ''}{delta}
                          </span>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        );
      })}
    </div>
  );
}
